import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Plus, Pencil, Trash2, ShieldCheck, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | 'new' | id
    const [form, setForm] = useState({ name: "", description: "", sort_order: 0 });
    const [saving, setSaving] = useState(false);

    const load = () => { setLoading(true); API.get("/categories").then(({ data }) => setItems(data)).catch(e => setError(apiError(e))).finally(() => setLoading(false)); };
    useEffect(load, []);

    const startNew = () => { setForm({ name: "", description: "", sort_order: items.length + 1 }); setEditing("new"); };
    const startEdit = (c) => { setForm({ name: c.name, description: c.description || "", sort_order: c.sort_order || 0 }); setEditing(c.id); };
    const cancel = () => { setEditing(null); setForm({ name: "", description: "", sort_order: 0 }); };

    const save = async () => {
        setSaving(true);
        try {
            if (editing === "new") { await API.post("/categories", form); toast.success("Categoria creata"); }
            else { await API.patch(`/categories/${editing}`, form); toast.success("Categoria aggiornata"); }
            cancel(); load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    const remove = async (id) => {
        try { await API.delete(`/categories/${id}`); toast.success("Categoria eliminata"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const canSave = form.name.length >= 2 && !saving;

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <div className="mt-1 flex items-center justify-between">
                <h1 className="font-serif text-3xl font-semibold" data-testid="admin-cats-title">Categorie</h1>
                {editing === null && <Button onClick={startNew} data-testid="admin-cats-new"><Plus className="size-4 mr-2" /> Nuova categoria</Button>}
            </div>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            {editing !== null && (
                <Card className="mt-6 border-primary/30" data-testid="admin-cats-form">
                    <CardContent className="pt-6 space-y-4">
                        <div className="grid gap-3 md:grid-cols-[1fr_120px]">
                            <div className="space-y-2">
                                <Label>Nome *</Label>
                                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} maxLength={80} data-testid="cat-name" />
                            </div>
                            <div className="space-y-2">
                                <Label>Ordine</Label>
                                <Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })} data-testid="cat-order" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Descrizione</Label>
                            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} maxLength={200} data-testid="cat-desc" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={cancel}><X className="size-4 mr-1" /> Annulla</Button>
                            <Button onClick={save} disabled={!canSave} data-testid="cat-save">
                                {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
                                Salva
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="mt-6 space-y-2" data-testid="admin-cats-list">
                {loading ? <div className="text-center py-10"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                    : items.map(c => (
                        <div key={c.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`cat-${c.id}`}>
                            <span className="size-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">{c.sort_order}</span>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-slate-900">{c.name}</div>
                                {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
                            </div>
                            {c.is_default && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">predefinita</span>}
                            <Button size="sm" variant="ghost" onClick={() => startEdit(c)} data-testid={`cat-edit-${c.id}`}><Pencil className="size-4" /></Button>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`cat-delete-${c.id}`}><Trash2 className="size-4" /></Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Eliminare "{c.name}"?</AlertDialogTitle>
                                        <AlertDialogDescription>Se la categoria è usata da domande o articoli l'operazione verrà bloccata.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => remove(c.id)} className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    ))}
            </div>
        </div>
    );
}
